import { Controller } from "@hotwired/stimulus"

// Serializes the selected recurrence radio/options to a single RRULE and
// writes it into the hidden `event_series[recurrence_rule]` field. Also
// fetches a preview of the next 5 occurrences from the server and
// renders them in the aria-live region.
export default class extends Controller {
  static targets = [
    "mode", "output", "startsDate", "startsTime",
    "monthlyDayPanel", "monthlyDayInput",
    "monthlyWeekdayPanel", "monthlyOrdinalInput", "monthlyWeekdayInput",
    "customPanel", "customInput",
    "preview"
  ]
  static values = { previewUrl: String, initialMode: String }

  connect() {
    // No-op on connect — server already writes the initial rule into the
    // hidden field. Just refresh the preview.
    this.updatePanels()
    this.refresh()
  }

  modeChanged() {
    this.updatePanels()
    this.refresh()
  }

  updatePanels() {
    const mode = this.currentMode
    this.monthlyDayPanelTarget.classList.toggle("hidden",    mode !== "monthly_day")
    this.monthlyWeekdayPanelTarget.classList.toggle("hidden", mode !== "monthly_weekday")
    this.customPanelTarget.classList.toggle("hidden",         mode !== "custom")
  }

  refresh() {
    const rule = this.buildRule()
    this.outputTarget.value = rule
    this.fetchPreview(rule)
  }

  buildRule() {
    const mode = this.currentMode
    switch (mode) {
      case "daily":
        return "FREQ=DAILY"
      case "weekdays":
        return "FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR"
      case "weekly":
        return `FREQ=WEEKLY;BYDAY=${this.icalDayForStartsDate()}`
      case "biweekly":
        return `FREQ=WEEKLY;INTERVAL=2;BYDAY=${this.icalDayForStartsDate()}`
      case "monthly_day": {
        const day = this.hasMonthlyDayInputTarget ? parseInt(this.monthlyDayInputTarget.value, 10) : null
        if (!day) return ""
        return `FREQ=MONTHLY;BYMONTHDAY=${day}`
      }
      case "monthly_weekday": {
        const ordinal = this.monthlyOrdinalInputTarget.value
        const weekday = this.monthlyWeekdayInputTarget.value
        return `FREQ=MONTHLY;BYDAY=${ordinal}${weekday}`
      }
      case "custom":
        return (this.customInputTarget.value || "").trim()
      case "none":
        return ""
      default:
        return ""
    }
  }

  get currentMode() {
    const checked = this.modeTargets.find(r => r.checked)
    return checked ? checked.value : this.initialModeValue || "weekly"
  }

  icalDayForStartsDate() {
    const value = this.hasStartsDateTarget ? this.startsDateTarget.value : ""
    const date  = value ? new Date(value) : new Date()
    const days  = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"]
    return days[date.getDay()]
  }

  fetchPreview(rule) {
    if (!this.hasPreviewTarget) return
    if (this.currentMode === "none") {
      this.renderError("Vælg gentagelse, før serien kan udgives.")
      return
    }
    if (!rule) {
      this.renderError("Vælg en gyldig gentagelsesregel")
      return
    }
    if (this.hasStartsDateTarget && !this.startsDateTarget.value) {
      this.renderError("Vælg en startdato for at se datoerne.")
      return
    }

    const url = new URL(this.previewUrlValue, window.location.origin)
    url.searchParams.set("recurrence_rule", rule)
    if (this.hasStartsDateTarget) url.searchParams.set("starts_date", this.startsDateTarget.value)
    if (this.hasStartsTimeTarget) url.searchParams.set("starts_time", this.startsTimeTarget.value)

    fetch(url, { headers: { "Accept": "text/html" } })
      .then(res => res.text())
      .then(html => { this.previewTarget.innerHTML = html })
      .catch(() => this.renderError("Kunne ikke hente forhåndsvisning"))
  }

  renderError(message) {
    this.previewTarget.innerHTML = `<li class="text-cirkler-orange font-semibold">${message}</li>`
  }
}
